import errorTracker from "~/utils/errorTracker";

/**
 * Vite rejects a dynamic import when the `<link rel="stylesheet">` it injects
 * for a CSS dependency fires `error`, which drops the whole SDK to the app
 * ErrorBoundary. WebKit aborts in-flight subresource requests routinely, so
 * that event is not a reason to discard a route that would otherwise render.
 *
 * Only stylesheet failures can be swallowed: a failed module fetch comes
 * through the same event, and preventing it leaves `React.lazy` with
 * `undefined` instead of a component.
 */
const CSS_PRELOAD_ERROR = /^Unable to preload CSS for (.+)$/;

const MAX_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 500;

const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const resolveHref = (href: string) => new URL(href, document.baseURI).href;

const getCspNonce = () => {
  const meta = document.querySelector<HTMLMetaElement>(
    "meta[property=csp-nonce]",
  );

  return meta?.nonce || meta?.getAttribute("nonce");
};

const isApplied = (href: string) =>
  Array.from(document.styleSheets).some((sheet) => sheet.href === href);

// The preload helper treats a matching link already in the document as proof
// the dependency is handled, so the failed one has to go first.
const dropFailedLinks = (href: string) => {
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
    .forEach((link) => {
      if (link.href === href) link.remove();
    });
};

const request = (href: string) =>
  new Promise<void>((resolve, reject) => {
    const link = document.createElement("link");
    const nonce = getCspNonce();

    link.rel = "stylesheet";
    link.crossOrigin = "";
    link.href = href;

    if (nonce) link.setAttribute("nonce", nonce);

    link.addEventListener("load", () => resolve());
    link.addEventListener("error", () => {
      link.remove();
      reject(new Error(href));
    });

    document.head.appendChild(link);
  });

const inFlight = new Map<string, Promise<void>>();

const recover = async (href: string) => {
  if (isApplied(href)) return;

  dropFailedLinks(href);

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      await request(href);

      errorTracker.addBreadCrumb(
        "css-preload",
        "Stylesheet recovered after a preload error",
        { href, attempt },
      );

      return;
    } catch {
      if (attempt < MAX_ATTEMPTS) {
        await delay(RETRY_BASE_DELAY_MS * attempt);
      }
    }
  }

  // Worded so it does not match the chunk-load fingerprint: this counts the
  // failures that survived every retry.
  errorTracker.captureException(
    new Error(
      `Stylesheet unrecoverable after ${MAX_ATTEMPTS} attempts: ${href}`,
    ),
  );
};

export const registerCssPreloadRecovery = () => {
  window.addEventListener("vite:preloadError", (event) => {
    const { payload } = event;
    const message =
      payload instanceof Error ? payload.message : String(payload);
    const href = CSS_PRELOAD_ERROR.exec(message)?.[1];

    if (!href) return;

    event.preventDefault();

    const resolved = resolveHref(href);

    if (inFlight.has(resolved)) return;

    inFlight.set(
      resolved,
      recover(resolved).finally(() => {
        inFlight.delete(resolved);
      }),
    );
  });
};
